import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HelpCircle,
  Mail,
  MessageCircle,
  MessagesSquare,
  Phone,
  RefreshCw,
  CheckCircle2,
  Clock,
  Ticket,
  Send,
  Lock,
  Tag,
  AlertCircle,
  User,
  X,
} from 'lucide-react';
import { useState } from 'react';

import type {
  SupportTicket,
  SupportTicketCategory,
  SupportTicketDetail,
  SupportTicketPriority,
  SupportTicketStatus,
} from '@terra/api-client';
import { Badge, EmptyState, Spinner } from '@terra/ui';

import { api } from '../lib/api';

// ══════════════════════════════════════════════════════════════════
// SUPPORT — Conversations / Product Queries / Contacts
// ══════════════════════════════════════════════════════════════════

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  OPEN: 'success',
  PENDING: 'warning',
  RESOLVED: 'neutral',
  CLOSED: 'neutral',
  PUBLISHED: 'success',
  REJECTED: 'danger',
  READ: 'neutral',
  REPLIED: 'success',
};

// ── Product Conversations ─────────────────────────────────────────

function SupportTabs({
  tabs,
  active,
  onChange,
}: {
  tabs: string[];
  active: string;
  onChange: (t: string) => void;
}) {
  return (
    <div className="border-border/60 bg-surface-subtle/30 mb-6 flex flex-wrap items-center gap-2 rounded-2xl border p-2 shadow-sm">
      {tabs.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => onChange(t)}
          className={`flex-1 rounded-xl px-4 py-2.5 text-[13px] font-bold transition-all sm:flex-none ${
            active === t
              ? 'bg-surface text-ink border-border/60 border shadow-sm'
              : 'text-ink-muted hover:bg-surface-subtle/50 hover:text-ink border border-transparent'
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

export function ProductSupportPage() {
  const [tab, setTab] = useState('Conversations');
  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">Product Support</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Manage private buyer-seller conversations and public product queries.
          </p>
        </div>
      </div>
      <SupportTabs tabs={['Conversations', 'Public Queries']} active={tab} onChange={setTab} />
      {tab === 'Conversations' && <ProductConversationsPage />}
      {tab === 'Public Queries' && <ProductQueriesPage />}
    </div>
  );
}

function ProductConversationsPage() {
  const { data: conversations, isLoading } = useQuery({
    queryKey: ['admin-product-conversations'],
    queryFn: () => api.support.productConversations.listAdmin(),
  });

  const rows = conversations ?? [];

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
          <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
            <MessagesSquare className="h-4 w-4" strokeWidth={2} />
          </div>
          <span className="text-ink text-[14px] font-bold">All Conversations</span>
        </div>

        <div className="p-0">
          {isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 m-5 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<MessagesSquare className="text-ink-muted h-10 w-10" />}
                title="No conversations yet"
                description="No product conversations have been started yet."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-20 py-3 pl-5 pr-4">ID</th>
                    <th className="py-3 pr-4">Subject</th>
                    <th className="py-3 pr-4 text-right">Date</th>
                    <th className="py-3 pr-5 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {rows.map((c: any) => (
                    <tr key={c.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        ...{c.id.slice(-6)}
                      </td>
                      <td className="text-ink py-3 pr-4 font-semibold">{c.subject}</td>
                      <td className="text-ink-muted py-3 pr-4 text-right text-[12px] font-medium">
                        {format(new Date(c.createdAt), 'MMM d, yyyy')}
                      </td>
                      <td className="py-3 pr-5 text-center">
                        <Badge tone={STATUS_TONE[c.status] ?? 'neutral'}>{c.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Product Queries ───────────────────────────────────────────────

function ProductQueriesPage() {
  const { data: queries, isLoading } = useQuery({
    queryKey: ['admin-product-queries'],
    queryFn: () => api.support.productQueries.listAdmin(),
  });

  const rows = queries ?? [];

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
          <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
            <HelpCircle className="h-4 w-4" strokeWidth={2} />
          </div>
          <span className="text-ink text-[14px] font-bold">All Product Queries</span>
        </div>

        <div className="p-0">
          {isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 m-5 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<HelpCircle className="text-ink-muted h-10 w-10" />}
                title="No queries yet"
                description="No product queries have been submitted yet."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-20 py-3 pl-5 pr-4">ID</th>
                    <th className="py-3 pr-4">Question</th>
                    <th className="py-3 pr-4">Answer</th>
                    <th className="py-3 pr-5 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {rows.map((q: any) => (
                    <tr key={q.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        ...{q.id.slice(-6)}
                      </td>
                      <td className="max-w-xs py-3 pr-4">
                        <p className="text-ink truncate font-semibold">{q.question}</p>
                      </td>
                      <td className="max-w-xs py-3 pr-4">
                        {q.answer ? (
                          <p className="text-ink-muted truncate text-[12px] font-medium">
                            {q.answer}
                          </p>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[12px] font-bold text-amber-600">
                            <Clock className="h-3 w-3" /> Awaiting answer
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-5 text-center">
                        <Badge tone={STATUS_TONE[q.status] ?? 'neutral'}>{q.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Contacts ──────────────────────────────────────────────────────

const CONTACT_STATUS_STYLE: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  READ: 'bg-blue-50 text-blue-700 border-blue-200',
  REPLIED: 'bg-green-50 text-green-700 border-green-200',
};

export function ContactsPage() {
  const queryClient = useQueryClient();

  const { data: contacts, isLoading } = useQuery({
    queryKey: ['admin-contacts'],
    queryFn: () => api.support.contacts.listAll(),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({
      contactId,
      status,
    }: {
      contactId: string;
      status: 'PENDING' | 'READ' | 'REPLIED';
    }) => api.support.contacts.updateStatus(contactId, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-contacts'] });
    },
  });

  const rows = contacts ?? [];

  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <MessageCircle className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink text-[14px] font-bold">All Contact Messages</span>
            {rows.length > 0 && (
              <span className="text-ink-muted text-[12px] font-bold">({rows.length})</span>
            )}
          </div>
          <button
            onClick={() => void queryClient.invalidateQueries({ queryKey: ['admin-contacts'] })}
            className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-10 items-center justify-center gap-2 rounded-xl border px-4 text-[13px] font-bold shadow-sm transition-all"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="p-0">
          {isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 m-5 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<Mail className="text-ink-muted h-10 w-10" />}
                title="No contact messages"
                description="No contact messages have been received yet."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Sender</th>
                    <th className="py-3 pr-4">Contact</th>
                    <th className="py-3 pr-4">Message</th>
                    <th className="py-3 pr-4 text-center">Status</th>
                    <th className="py-3 pr-4 text-right">Date</th>
                    <th className="py-3 pr-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {rows.map((contact: any, idx: number) => (
                    <tr key={contact.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="py-3 pr-4">
                        <span className="text-ink font-semibold">{contact.name}</span>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="space-y-0.5">
                          <a
                            href={`mailto:${contact.email}`}
                            className="flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:underline"
                          >
                            <Mail className="h-3 w-3 shrink-0" />
                            {contact.email}
                          </a>
                          {contact.phone && (
                            <div className="text-ink-muted flex items-center gap-1 text-[12px] font-medium">
                              <Phone className="h-3 w-3 shrink-0" />
                              {contact.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="max-w-[220px] py-3 pr-4">
                        <p
                          className="text-ink-muted truncate text-[12px] font-medium"
                          title={contact.message}
                        >
                          {contact.message}
                        </p>
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <span
                          className={`inline-flex items-center rounded-lg border px-2.5 py-0.5 text-[11px] font-extrabold ${
                            CONTACT_STATUS_STYLE[contact.status] ??
                            'bg-surface-subtle text-ink-muted border-border/40'
                          }`}
                        >
                          {contact.status}
                        </span>
                      </td>
                      <td className="text-ink-muted whitespace-nowrap py-3 pr-4 text-right text-[12px] font-medium">
                        {format(new Date(contact.createdAt), 'MMM d, yyyy')}
                      </td>
                      <td className="py-3 pr-5">
                        <div className="flex items-center justify-end gap-2">
                          {contact.status === 'PENDING' && (
                            <button
                              onClick={() =>
                                updateStatusMutation.mutate({
                                  contactId: contact.id,
                                  status: 'READ',
                                })
                              }
                              disabled={updateStatusMutation.isPending}
                              className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-8 items-center rounded-lg border px-3 text-[11px] font-bold transition-colors disabled:opacity-40"
                            >
                              Mark Read
                            </button>
                          )}
                          {contact.status !== 'REPLIED' && (
                            <button
                              onClick={() =>
                                updateStatusMutation.mutate({
                                  contactId: contact.id,
                                  status: 'REPLIED',
                                })
                              }
                              disabled={updateStatusMutation.isPending}
                              className="bg-ink hover:bg-ink/90 flex h-8 items-center gap-1.5 rounded-lg px-3 text-[11px] font-bold text-white shadow-sm transition-colors disabled:opacity-40"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              Mark Replied
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Support Tickets (Queue) ───────────────────────────────────────

const TICKET_STATUS_TONE: Record<string, 'warning' | 'danger' | 'success' | 'neutral'> = {
  OPEN: 'warning',
  PENDING_CUSTOMER: 'neutral',
  IN_PROGRESS: 'warning',
  ESCALATED: 'danger',
  RESOLVED: 'success',
  CLOSED: 'neutral',
};

const TICKET_PRIORITY_TONE: Record<string, 'warning' | 'danger' | 'neutral'> = {
  LOW: 'neutral',
  NORMAL: 'neutral',
  HIGH: 'warning',
  URGENT: 'danger',
};

const TICKET_STATUSES: SupportTicketStatus[] = [
  'OPEN',
  'ASSIGNED',
  'IN_PROGRESS',
  'WAITING_FOR_CUSTOMER',
  'ESCALATED',
  'RESOLVED',
  'CLOSED',
];

const _TICKET_PRIORITIES: SupportTicketPriority[] = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

export function SupportPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<{
    status?: SupportTicketStatus;
    category?: SupportTicketCategory;
  }>({});
  const [activeTicket, setActiveTicket] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);

  const { data: ticketsResponse, isLoading } = useQuery({
    queryKey: ['admin-tickets', filter],
    queryFn: () => api.support.adminList({ ...filter, limit: 100 }),
  });

  const { data: ticketDetail, isLoading: isLoadingDetail } = useQuery<SupportTicketDetail>({
    queryKey: ['admin-ticket', activeTicket],
    queryFn: () => api.support.adminGet(activeTicket!),
    enabled: !!activeTicket,
  });

  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof api.support.adminUpdate>[1];
    }) => api.support.adminUpdate(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-tickets'] });
      if (activeTicket) void qc.invalidateQueries({ queryKey: ['admin-ticket', activeTicket] });
    },
  });

  const sendReply = useMutation({
    mutationFn: () => api.support.adminReply(activeTicket!, { body: reply, internal }),
    onSuccess: () => {
      setReply('');
      setInternal(false);
      void qc.invalidateQueries({ queryKey: ['admin-ticket', activeTicket] });
    },
  });

  const tickets = ticketsResponse ?? [];

  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-wrap items-center gap-2">
        {['', ...TICKET_STATUSES].map((s) => {
          const isActive = filter.status === (s || undefined);
          return (
            <button
              key={s || 'all'}
              onClick={() => {
                setFilter((f) => {
                  const next = { ...f };
                  if (s) next.status = s as SupportTicketStatus;
                  else delete next.status;
                  return next;
                });
              }}
              className={`h-8 rounded-xl px-3 text-[12px] font-bold transition-all ${
                isActive
                  ? 'bg-ink text-white shadow-sm'
                  : 'bg-surface border-border/60 text-ink hover:bg-surface-subtle border'
              }`}
            >
              {s ? s.replace(/_/g, ' ') : 'All Statuses'}
            </button>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
        {/* Left: Ticket List */}
        <div className="border-border/60 bg-surface self-start overflow-hidden rounded-2xl border shadow-sm">
          <div className="border-border/60 bg-surface-subtle/30 flex items-center gap-3 border-b px-5 py-4">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <Ticket className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink text-[14px] font-bold">Ticket Queue</span>
          </div>

          <div className="p-0">
            {isLoading ? (
              <div className="grid min-h-[300px] place-items-center">
                <Spinner size="lg" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="border-border/60 bg-surface-subtle/30 m-5 rounded-2xl border border-dashed py-12">
                <EmptyState
                  icon={<Ticket className="text-ink-muted h-10 w-10" />}
                  title="No tickets"
                  description="Tickets matching this filter will appear here."
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                      <th className="w-16 py-3 pl-5 pr-4">#</th>
                      <th className="py-3 pr-4">Subject</th>
                      <th className="py-3 pr-4">Category</th>
                      <th className="py-3 pr-4">Priority</th>
                      <th className="py-3 pr-4 text-center">Status</th>
                      <th className="py-3 pr-5 text-right">Updated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-border/40 divide-y text-[13px]">
                    {tickets.map((t: SupportTicket) => (
                      <tr
                        key={t.id}
                        className={`cursor-pointer transition-colors ${
                          activeTicket === t.id ? 'bg-orange-50/50' : 'hover:bg-surface-subtle/30'
                        }`}
                        onClick={() => setActiveTicket(t.id)}
                      >
                        <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                          {t.ticketNumber}
                        </td>
                        <td className="text-ink py-3 pr-4 font-semibold">{t.subject}</td>
                        <td className="text-ink-muted py-3 pr-4 text-[12px] font-medium">
                          {t.category.replace(/_/g, ' ')}
                        </td>
                        <td className="py-3 pr-4">
                          <Badge tone={TICKET_PRIORITY_TONE[t.priority] ?? 'neutral'}>
                            {t.priority}
                          </Badge>
                        </td>
                        <td className="py-3 pr-4 text-center">
                          <Badge tone={TICKET_STATUS_TONE[t.status] ?? 'neutral'}>
                            {t.status.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="text-ink-muted whitespace-nowrap py-3 pr-5 text-right text-[12px] font-medium">
                          {format(new Date(t.updatedAt), 'MMM d, yyyy')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right: Ticket Detail Sidebar */}
        <AnimatePresence mode="popLayout">
          {activeTicket && (
            <motion.aside
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              {isLoadingDetail ? (
                <div className="border-border/60 bg-surface grid h-[500px] place-items-center rounded-2xl border shadow-sm">
                  <Spinner size="lg" />
                </div>
              ) : ticketDetail ? (
                <div className="border-border/60 bg-surface flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-2xl border shadow-sm">
                  {/* Sidebar Header */}
                  <div className="border-border/60 bg-surface-subtle/30 border-b p-5">
                    <div className="mb-3 flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="mb-1 flex items-center gap-2">
                          <span className="text-ink-muted border-border/40 bg-surface rounded-md border px-2 py-0.5 text-[11px] font-extrabold uppercase tracking-wider">
                            {ticketDetail.ticketNumber}
                          </span>
                          <Badge tone={TICKET_PRIORITY_TONE[ticketDetail.priority] ?? 'neutral'}>
                            {ticketDetail.priority}
                          </Badge>
                        </div>
                        <h3 className="text-ink text-[16px] font-extrabold leading-tight">
                          {ticketDetail.subject}
                        </h3>
                      </div>
                      <button
                        onClick={() => setActiveTicket(null)}
                        className="text-ink-muted hover:bg-surface hover:text-ink hover:border-border/60 flex h-8 w-8 items-center justify-center rounded-lg border border-transparent transition-colors"
                      >
                        <X className="h-4 w-4" strokeWidth={2.5} />
                      </button>
                    </div>

                    <div className="border-border/40 flex flex-wrap gap-2 border-t pt-2">
                      <div className="text-ink-muted bg-surface border-border/40 flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[12px] font-bold">
                        <Tag className="h-3.5 w-3.5" />
                        {ticketDetail.category.replace(/_/g, ' ')}
                      </div>
                      <select
                        className={`h-7 cursor-pointer rounded-lg border px-2 text-[12px] font-bold outline-none transition-colors ${
                          ticketDetail.status === 'RESOLVED'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : ticketDetail.status === 'ESCALATED'
                              ? 'border-red-200 bg-red-50 text-red-700'
                              : 'bg-surface text-ink-muted border-border/60 hover:border-border'
                        }`}
                        value={ticketDetail.status}
                        onChange={(e) =>
                          update.mutate({
                            id: ticketDetail.id,
                            body: { status: e.target.value as SupportTicketStatus },
                          })
                        }
                      >
                        {TICKET_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Messages Feed */}
                  <div className="bg-surface-subtle/10 flex-1 space-y-4 overflow-y-auto p-5">
                    {ticketDetail.messages.map((m) => {
                      const isInternal = m.internal;
                      const isMe = m.authorId !== ticketDetail.subjectUserId; // Very basic check, assuming author !== ticket creator is support agent
                      return (
                        <div
                          key={m.id}
                          className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
                        >
                          <div className="mb-1 flex items-center gap-1.5 px-1">
                            {isInternal && <Lock className="h-3 w-3 text-amber-500" />}
                            <span className="text-ink-muted text-[11px] font-bold">
                              {isMe ? 'Support Agent' : 'Customer'}
                            </span>
                            <span className="text-ink-subtle text-[10px] font-medium">
                              {format(new Date(m.createdAt), 'h:mm a, MMM d')}
                            </span>
                          </div>
                          <div
                            className={`max-w-[90%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed ${
                              isInternal
                                ? 'rounded-tr-sm border border-amber-200 bg-amber-50 text-amber-900'
                                : isMe
                                  ? 'bg-ink rounded-tr-sm text-white'
                                  : 'bg-surface text-ink border-border/60 rounded-tl-sm border shadow-sm'
                            }`}
                          >
                            <p className="whitespace-pre-wrap">{m.body}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Reply Box */}
                  <div className="border-border/60 bg-surface border-t p-4">
                    <div
                      className={`focus-within:border-ink/50 overflow-hidden rounded-xl border transition-colors focus-within:ring-1 ${internal ? 'border-amber-300 bg-amber-50/30 focus-within:ring-amber-400' : 'border-border/60 bg-surface'}`}
                    >
                      <textarea
                        className="min-h-[80px] w-full resize-none bg-transparent px-4 py-3 text-[13px] outline-none"
                        placeholder={
                          internal
                            ? "Write an internal note (customers won't see this)..."
                            : 'Type your reply to the customer...'
                        }
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                      />
                      <div className="bg-surface-subtle/50 border-border/40 flex items-center justify-between border-t px-3 py-2">
                        <label className="group flex cursor-pointer items-center gap-2">
                          <div
                            className={`grid h-5 w-5 place-items-center rounded border transition-colors ${internal ? 'border-amber-500 bg-amber-500' : 'bg-surface border-border/60 group-hover:border-border'}`}
                          >
                            {internal && (
                              <CheckCircle2 className="h-3 w-3 text-white" strokeWidth={3} />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={internal}
                            onChange={(e) => setInternal(e.target.checked)}
                          />
                          <span className="text-ink-muted group-hover:text-ink text-[12px] font-bold transition-colors">
                            Internal Note
                          </span>
                        </label>
                        <button
                          onClick={() => reply.trim() && sendReply.mutate()}
                          disabled={!reply.trim() || sendReply.isPending}
                          className="bg-ink hover:bg-ink/90 flex h-8 items-center gap-1.5 rounded-lg px-4 text-[12px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50 disabled:hover:shadow-none"
                        >
                          {sendReply.isPending ? (
                            <Spinner size="sm" className="text-white" />
                          ) : (
                            <Send className="h-3.5 w-3.5" />
                          )}
                          Send
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-border/60 bg-surface grid h-[200px] place-items-center rounded-2xl border shadow-sm">
                  <p className="text-ink-muted text-[13px] font-medium">Ticket not found.</p>
                </div>
              )}
            </motion.aside>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
